/**
 * DashboardContext — React context for dashboard shell state that must reach
 * islands without threading through the BlockHost symbol boundary.
 *
 * BlockHost's IslandRenderer only passes props resolved from `#{local.*}`
 * symbol expressions in `ui.cogo.json`. Non-symbol state (analytics deep-link
 * slug/appName, onBack callback, etc.) cannot travel that path. This context
 * is provided by the shell wrapper outside the BlockHost mount — islands
 * consume it directly via `useContext(DashboardContext)` since React context
 * flows through the BlockHost tree naturally.
 *
 * Scope deliberately narrow: only holds state that originates OUTSIDE the
 * block's own route-switching logic. Anything derivable from `local.route`
 * belongs in the block's variables / symbol expressions, not here.
 */
import { createContext, useContext } from 'react'

export interface DashboardContextValue {
  /** Analytics deep-link slug — set by the `analytics-navigate` custom event
   *  or sessionStorage restore. `null` when no analytics context is active;
   *  the AnalyticsIsland falls back to OverviewPage in that case. */
  analyticsSlug: string | null
  /** Analytics app display name — paired with slug. Empty string when no
   *  analytics context is active. */
  analyticsAppName: string
  /** Callback invoked by the analytics page's Back button. Resets route to
   *  home and clears analytics state in the shell. */
  onAnalyticsBack: () => void
}

const DEFAULT: DashboardContextValue = {
  analyticsSlug: null,
  analyticsAppName: '',
  onAnalyticsBack: () => {
    /* no-op default — replaced by the shell's Provider */
  },
}

export const DashboardContext = createContext<DashboardContextValue>(DEFAULT)

export function useDashboardContext(): DashboardContextValue {
  return useContext(DashboardContext)
}
